import React, { useRef } from 'react';

// Interactive crop rectangle overlay. `rect` is normalized {x,y,w,h} in 0..1 of
// the frame it overlays (the parent must be positioned and exactly cover the
// frame). Drag the body to move; drag a corner (or edge, when free) to resize.
//
// `lockRatio` (w/h in the SAME normalized space) keeps the box at a fixed shape
// while resizing — pass null for a free/native crop. The caller owns the rect
// and persists it; we only clamp to the frame and a minimum size.
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const CORNERS = [
  { id: 'nw', left: true, top: true },
  { id: 'ne', left: false, top: true },
  { id: 'sw', left: true, top: false },
  { id: 'se', left: false, top: false },
];
const EDGES = [
  { id: 'n', axis: 'y', top: true },
  { id: 's', axis: 'y', top: false },
  { id: 'w', axis: 'x', left: true },
  { id: 'e', axis: 'x', left: false },
];

export default function CropBox({ rect, onChange, lockRatio = null, minSize = 0.08 }) {
  const overlayRef = useRef(null);

  if (!rect) return <div ref={overlayRef} className="cs-cropbox-overlay" />;

  // Pointer position in normalized frame coords (0..1), clamped.
  const ptNorm = (e) => {
    const host = overlayRef.current;
    const r = host.getBoundingClientRect();
    return {
      x: clamp((e.clientX - r.left) / r.width, 0, 1),
      y: clamp((e.clientY - r.top) / r.height, 0, 1),
    };
  };

  const drag = (e, onMove) => {
    e.preventDefault();
    e.stopPropagation();
    const move = (ev) => onMove(ptNorm(ev));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Move the whole box, keeping its size, clamped inside the frame.
  const onBodyDown = (e) => {
    const start = ptNorm(e);
    const orig = { ...rect };
    drag(e, (p) => {
      const nx = clamp(orig.x + (p.x - start.x), 0, 1 - orig.w);
      const ny = clamp(orig.y + (p.y - start.y), 0, 1 - orig.h);
      onChange({ x: nx, y: ny, w: orig.w, h: orig.h });
    });
  };

  // Resize from a corner: the opposite corner is the anchor.
  const onCornerDown = (e, c) => {
    const anchor = { x: c.left ? rect.x + rect.w : rect.x, y: c.top ? rect.y + rect.h : rect.y };
    drag(e, (p) => {
      const maxW = c.left ? anchor.x : 1 - anchor.x;
      const maxH = c.top ? anchor.y : 1 - anchor.y;
      let w = clamp(Math.abs(p.x - anchor.x), minSize, maxW);
      let h = clamp(Math.abs(p.y - anchor.y), minSize, maxH);
      if (lockRatio) {
        // Grow to whichever axis the pointer demands, then fit inside bounds.
        if (w >= h * lockRatio) h = w / lockRatio; else w = h * lockRatio;
        if (w > maxW) { w = maxW; h = w / lockRatio; }
        if (h > maxH) { h = maxH; w = h * lockRatio; }
      }
      const x = c.left ? anchor.x - w : anchor.x;
      const y = c.top ? anchor.y - h : anchor.y;
      onChange({ x, y, w, h });
    });
  };

  // Resize from an edge (free mode only): one axis moves, opposite edge anchors.
  const onEdgeDown = (e, ed) => {
    drag(e, (p) => {
      const next = { ...rect };
      if (ed.axis === 'x') {
        const anchorX = ed.left ? rect.x + rect.w : rect.x;
        const w = clamp(Math.abs(p.x - anchorX), minSize, ed.left ? anchorX : 1 - anchorX);
        next.x = ed.left ? anchorX - w : anchorX;
        next.w = w;
      } else {
        const anchorY = ed.top ? rect.y + rect.h : rect.y;
        const h = clamp(Math.abs(p.y - anchorY), minSize, ed.top ? anchorY : 1 - anchorY);
        next.y = ed.top ? anchorY - h : anchorY;
        next.h = h;
      }
      onChange(next);
    });
  };

  return (
    <div ref={overlayRef} className="cs-cropbox-overlay">
      <div
        className="cs-cropbox"
        onPointerDown={onBodyDown}
        style={{
          left: `${rect.x * 100}%`,
          top: `${rect.y * 100}%`,
          width: `${rect.w * 100}%`,
          height: `${rect.h * 100}%`,
        }}
      >
        <span className="cs-cropbox-third v1" />
        <span className="cs-cropbox-third v2" />
        <span className="cs-cropbox-third h1" />
        <span className="cs-cropbox-third h2" />
        {!lockRatio && EDGES.map((ed) => (
          <span
            key={ed.id}
            className={`cs-cropbox-edge cs-cropbox-edge-${ed.id}`}
            onPointerDown={(e) => onEdgeDown(e, ed)}
          />
        ))}
        {CORNERS.map((c) => (
          <span
            key={c.id}
            className={`cs-cropbox-grip cs-cropbox-grip-${c.id}`}
            onPointerDown={(e) => onCornerDown(e, c)}
          />
        ))}
      </div>
    </div>
  );
}
